import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaPrint,
  FaSpinner,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import useApp from "../../hooks/useAppContext";
import { formatarDiaHora } from "../../services/conversaoTimer";
import api from "../../services/axios";
import logo from "../../assets/SBF-Logo-1.png";
import "./admPatrocinadoresList.css";
import "./print.css";

const LABELS_TIPO = {
  reuniao_negocio: "Reunião",
  m_m: "M&M",
  conteudo_patrocinado: "Conteúdo patrocinado",
  conteudo_editorial: "Conteúdo editorial",
  extraordinaria: "Especial",
};

const MENSAGEM_INFORMA_MARKETS = "Comercial Informa Markets";

function idUsuarioAp(ap) {
  return Number(ap?.id_usuario ?? ap?.idUsuario ?? 0);
}

function getOutrosParticipantes(agenda, userId) {
  const idNum = parseInt(userId, 10);
  return (agenda?.participantes || []).filter(
    (ap) => idUsuarioAp(ap) !== idNum
  );
}

function textoOuTraco(valor) {
  if (!valor || valor === "null") return "—";
  return valor;
}

function normalizarHora(valor) {
  return String(valor || "").trim().substring(0, 5);
}

function idSlotDaAgenda(agenda, reuniao) {
  const bruto =
    agenda?.id_slot ?? agenda?.idSlot ?? reuniao?.id_slot ?? reuniao?.idSlot;
  const id = Number(bruto);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function diaLabelDeDataIso(dataIso) {
  const m = String(dataIso || "")
    .substring(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
}

function resolverIdSlotReuniao(reuniao, slots) {
  const agenda = reuniao?.agenda;
  if (!agenda) return null;

  const idSlot = idSlotDaAgenda(agenda, reuniao);
  if (idSlot) return idSlot;

  const horarioCompleto = formatarDiaHora(
    agenda.dt_hora_inicio || agenda.dtHoraInicio
  );
  if (!horarioCompleto) return null;

  const dia = horarioCompleto.substring(0, 10);
  const hora = normalizarHora(horarioCompleto.substring(11, 16));
  const slot = (slots || []).find((s) => {
    const diaSlot = diaLabelDeDataIso(s.data);
    return diaSlot === dia && normalizarHora(s.horario_inicio) === hora;
  });

  return slot?.id ?? null;
}

/**
 * Conta reuniões exibíveis do patrocinador por slot
 * (mesmo critério da impressão: precisa ter o outro participante).
 */
function contarReunioesPorSlot(reunioes, slots, userId) {
  const contagem = new Map();

  for (const r of reunioes || []) {
    const agenda = r?.agenda;
    if (!agenda) continue;
    if (agenda.tipo !== "reuniao_negocio" && agenda.tipo !== "m_m") continue;
    if (!getOutrosParticipantes(agenda, userId).length) continue;

    const idSlot = resolverIdSlotReuniao(r, slots);
    if (!idSlot) continue;

    contagem.set(idSlot, (contagem.get(idSlot) || 0) + 1);
  }

  return contagem;
}

function montarItemInformaMarkets(slot) {
  const dia = diaLabelDeDataIso(slot.data);
  const hora = normalizarHora(slot.horario_inicio);

  return {
    key: `informa-markets-${slot.id}`,
    tipo: "reuniao_negocio",
    dia,
    horario: `${dia} ${hora}`,
    titulo: MENSAGEM_INFORMA_MARKETS,
    cargo: "—",
    empresa: "—",
    kind: "informa",
    isInforma: true,
    isMm: false,
    isExtra: false,
    isAlmoco: false,
    local: "",
  };
}

/**
 * Escolhe o slot para a reunião Informa Markets na impressão.
 * Advance (cota simultânea ≥ 2): prioriza o primeiro slot com exatamente
 * 1 reunião (ainda cabe a segunda). Senão, usa o primeiro slot com vaga.
 */
function escolherSlotInformaMarkets(slots, reunioes, userId, cotaSimultania) {
  if (!slots?.length) return null;

  const cota = Math.max(1, Number(cotaSimultania) || 1);
  const contagem = contarReunioesPorSlot(reunioes, slots, userId);

  if (cota >= 2) {
    const comUmaReuniao = slots.find((s) => (contagem.get(s.id) || 0) === 1);
    if (comUmaReuniao) return comUmaReuniao;
  }

  return slots.find((s) => (contagem.get(s.id) || 0) < cota) || null;
}

/**
 * Em agendas de patrocinador, reserva um slot com vaga para
 * a reunião institucional com a Informa Markets (apenas impressão).
 */
function inserirReuniaoInformaMarkets(
  itens,
  reunioes,
  slots,
  userId,
  cotaSimultania
) {
  const slotEscolhido = escolherSlotInformaMarkets(
    slots,
    reunioes,
    userId,
    cotaSimultania
  );
  if (!slotEscolhido) return itens;

  const novos = [...(itens || []), montarItemInformaMarkets(slotEscolhido)];
  novos.sort((a, b) =>
    String(a.horario || "").localeCompare(String(b.horario || ""))
  );
  return novos;
}

function montarItensImpressao(
  reunioes,
  userId,
  isPatrocinador,
  slots = [],
  cotaSimultania = 1
) {
  const itens = [];

  for (const r of reunioes || []) {
    const agenda = r?.agenda;
    if (!agenda?.tipo) continue;

    const horarioCompleto = formatarDiaHora(
      agenda.dt_hora_inicio || agenda.dtHoraInicio
    );
    const dia = horarioCompleto.substring(0, 10);
    const horario = horarioCompleto.substring(0, 16);

    if (agenda.tipo === "reuniao_negocio" || agenda.tipo === "m_m") {
      const outros = getOutrosParticipantes(agenda, userId);
      if (isPatrocinador && !outros.length) continue;

      const outro = outros[0]?.usuario;

      itens.push({
        key: r.id,
        tipo: agenda.tipo,
        dia,
        horario,
        titulo: (outro?.nome || agenda.nome || "—").toUpperCase(),
        cargo: textoOuTraco(outro?.cargo),
        empresa: textoOuTraco(outro?.empresa),
        kind: "reuniao",
        isInforma: false,
        isMm: agenda.tipo === "m_m",
        isExtra: agenda.nome === "Reunião de negócios*",
        isAlmoco: agenda.nome === "Reunião de almoço/jantar",
        local: agenda.local || "",
      });
      continue;
    }

    if (
      !isPatrocinador &&
      (agenda.tipo === "conteudo_patrocinado" ||
        agenda.tipo === "conteudo_editorial" ||
        agenda.tipo === "extraordinaria")
    ) {
      itens.push({
        key: r.id,
        tipo: agenda.tipo,
        dia,
        horario,
        titulo: (agenda.nome || "—").toUpperCase(),
        cargo: "—",
        empresa: "—",
        kind: agenda.tipo === "extraordinaria" ? "extraordinaria" : "conteudo",
        isInforma: false,
        isMm: false,
        isExtra: false,
        isAlmoco: false,
        local: agenda.local || "",
      });
    }
  }

  if (isPatrocinador) {
    return inserirReuniaoInformaMarkets(
      itens,
      reunioes,
      slots,
      userId,
      cotaSimultania
    );
  }

  return itens;
}

function agruparPorDia(itens) {
  const grupos = [];
  const mapa = new Map();

  for (const item of itens) {
    if (!mapa.has(item.dia)) {
      const grupo = { dia: item.dia, itens: [] };
      mapa.set(item.dia, grupo);
      grupos.push(grupo);
    }
    mapa.get(item.dia).itens.push(item);
  }

  return grupos;
}

const AdmImprimirReunioesConvidados = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setMostraMenuAdm } = useApp();

  const [convidado, setConvidado] = useState(null);
  const [reunioes, setReunioes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [isPatrocinador, setIsPatrocinador] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const carregaUsuario = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const [resp, respSlots] = await Promise.all([
        api.get(`/usuarios-get/${id}`),
        api.get("/slots").catch(() => null),
      ]);
      const dados = resp.data.user;
      setConvidado(dados);
      setIsPatrocinador(Boolean(dados?.plano));

      if (Array.isArray(respSlots?.data?.dados)) {
        setSlots(respSlots.data.dados);
      } else {
        setSlots([]);
      }

      const ordenacao = [...(dados.agendaParticipante || [])]
        .filter((ap) => ap?.agenda)
        .sort((a, b) => {
          const dataA = new Date(
            a.agenda.dt_hora_inicio || a.agenda.dtHoraInicio
          );
          const dataB = new Date(
            b.agenda.dt_hora_inicio || b.agenda.dtHoraInicio
          );
          return dataA - dataB;
        });

      setReunioes(ordenacao);
    } catch (err) {
      console.error("Erro ao carregar agenda para impressão:", err);
      setError("Erro ao carregar a agenda do usuário");
      setConvidado(null);
      setReunioes([]);
      setSlots([]);
      setIsPatrocinador(false);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setMostraMenuAdm(false);
    carregaUsuario();

    return () => {
      setMostraMenuAdm(true);
    };
  }, [carregaUsuario, setMostraMenuAdm]);

  const itensAgenda = useMemo(() => {
    const cotaSimultania =
      convidado?.plano?.cota_simultania ??
      convidado?.plano?.cotaSimultania ??
      1;
    return montarItensImpressao(
      reunioes,
      id,
      isPatrocinador,
      slots,
      cotaSimultania
    );
  }, [reunioes, id, isPatrocinador, slots, convidado]);

  const gruposPorDia = useMemo(
    () => agruparPorDia(itensAgenda),
    [itensAgenda]
  );

  const colunaPrincipal = isPatrocinador ? "Convidado" : "Participante / Evento";
  const dataGeracao = useMemo(
    () =>
      new Date().toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    []
  );

  const handleImprimir = () => {
    window.print();
  };

  const handleVoltar = () => {
    if (id) {
      navigate(`/private/adm/convidados/reunioes/${id}`);
      return;
    }
    navigate(-1);
  };

  return (
    <div className="container-adm-main aip-page-wrap">
      <div className="apl-page aip-screen">
        <header className="apl-header aip-no-print" aria-label="Cabeçalho da impressão">
          <div className="apl-header-text">
            <h1>
              Imprimir agenda
              {convidado?.nome ? ` — ${convidado.nome}` : ""}
            </h1>
            <p>
              Pré-visualização da agenda para impressão. Use o botão Imprimir
              quando estiver pronto.
            </p>
          </div>

          <div className="apl-toolbar">
            <button
              type="button"
              className="apl-btn apl-btn--primary"
              onClick={handleImprimir}
              disabled={isLoading || !itensAgenda.length}
            >
              <FaPrint size={14} aria-hidden />
              Imprimir
            </button>
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={handleVoltar}
              title="Voltar para a agenda"
            >
              <FaArrowLeft size={14} aria-hidden />
              Voltar
            </button>
          </div>
        </header>

        {error ? (
          <div className="apl-alert aip-no-print" role="alert">
            {error}
          </div>
        ) : null}

        {isLoading ? (
          <div className="aip-loading-wrap aip-no-print" role="status" aria-live="polite">
            <div className="apl-card aip-loading-card">
              <div className="apl-loading">
                <FaSpinner className="fa-spin" size={28} aria-hidden />
                <span>Carregando agenda para impressão…</span>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {!isLoading && !error ? (
        <div id="printable" className="aip-print-doc">
          <div className="aip-print-header">
            <img src={logo} alt="SBF" className="aip-print-logo" />
            <div className="aip-print-title-block">
              <h1 className="aip-print-title">Agenda</h1>
              <p className="aip-print-name">{convidado?.nome || "—"}</p>
              {(convidado?.cargo || convidado?.empresa) && (
                <p className="aip-print-meta">
                  {[convidado?.cargo, convidado?.empresa]
                    .filter((v) => v && v !== "null")
                    .join(" · ")}
                </p>
              )}
            </div>
            <div className="aip-print-aside">
              <span className="aip-print-count">
                {itensAgenda.length}{" "}
                {itensAgenda.length === 1 ? "item" : "itens"}
              </span>
              <span className="aip-print-date">Gerado em {dataGeracao}</span>
            </div>
          </div>

          {itensAgenda.length === 0 ? (
            <div className="aip-print-empty">
              <p>Nenhuma reunião agendada</p>
            </div>
          ) : (
            gruposPorDia.map((grupo) => (
              <section key={grupo.dia} className="aip-print-day">
                <h2 className="aip-print-day-title">{grupo.dia}</h2>
                <table className="aip-print-table">
                  <thead>
                    <tr>
                      <th scope="col">Horário</th>
                      <th scope="col">{colunaPrincipal}</th>
                      {isPatrocinador ? <th scope="col">Cargo</th> : null}
                      {isPatrocinador ? (
                        <th scope="col">Instituição</th>
                      ) : null}
                      <th scope="col">Tipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupo.itens.map((item) =>
                      item.isInforma ? (
                        <tr key={item.key} className="aip-print-row--informa">
                          <td className="aip-print-horario">
                            {item.horario.substring(11)}
                          </td>
                          <td
                            colSpan={isPatrocinador ? 4 : 2}
                            className="aip-print-cell-informa"
                          >
                            {MENSAGEM_INFORMA_MARKETS}
                          </td>
                        </tr>
                      ) : (
                        <tr key={item.key}>
                          <td
                            className={
                              item.isMm
                                ? "aip-print-horario aip-print-horario--mm"
                                : "aip-print-horario"
                            }
                          >
                            {item.horario.substring(11)}
                          </td>
                          <td className="aip-print-cell-name">
                            <span className="aip-print-name-text">
                              {item.titulo}
                            </span>
                            {item.isExtra ? (
                              <span className="aip-print-tag aip-print-tag--warn">
                                Extra
                              </span>
                            ) : null}
                            {item.isAlmoco ? (
                              <span className="aip-print-tag aip-print-tag--warn">
                                Almoço/Jantar
                              </span>
                            ) : null}
                            {item.local ? (
                              <span className="aip-print-local">
                                Local: {item.local}
                              </span>
                            ) : null}
                          </td>
                          {isPatrocinador ? <td>{item.cargo}</td> : null}
                          {isPatrocinador ? <td>{item.empresa}</td> : null}
                          <td>
                            <span
                              className={
                                item.isMm
                                  ? "aip-print-tag aip-print-tag--mm"
                                  : "aip-print-tag"
                              }
                            >
                              {LABELS_TIPO[item.tipo] || item.tipo}
                            </span>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </section>
            ))
          )}

          <footer className="aip-print-footer">
            <span>
              {isPatrocinador
                ? "Agenda do patrocinador"
                : "Agenda do convidado"}
            </span>
            <span>SBF 2026</span>
          </footer>
        </div>
      ) : null}
    </div>
  );
};

export default AdmImprimirReunioesConvidados;
